import { useMemo } from "react";
import { AutovioCalendarEvent, DrivingLesson, isDrivingLesson } from "../model/autovioCalendarEvents";
import {
  isForBus,
  isForCar,
  isForMotorcycle,
  isForTractor,
  isForTruck,
  isWithTrailer,
} from "../model/DrivingLicenseClass";
import { DateTime } from "luxon";

export function useMissingVehiclesWarnings(events: undefined | AutovioCalendarEvent[]): {
  warningsByDay: Map</* "yyyy-MM-dd" */ string, string>;
  warningsByEventId: Map<string, string[]>;
} {
  // We only care about future events ...
  const now = DateTime.now();
  const relevantDrivingLessons = useMemo(
    () =>
      (events ?? []).filter(
        (it) => isDrivingLesson(it) && !it.endedAt && it.start > now && it.drivingLicenseClass,
      ) as Array<Defined<DrivingLesson, "drivingLicenseClass">>,
    [events],
  );
  const result = useMemo(() => {
    const warningsByEventId = new Map<string, string[]>();
    const eventsWithMissingVehiclesByDay = new Map<string, number>();
    for (const drivingLesson of relevantDrivingLessons) {
      const warnings = getMissingVehicleWarnings(drivingLesson);
      if (warnings.length > 0) {
        warningsByEventId.set(drivingLesson.id, warnings);
        const day = drivingLesson.start.toISODate();
        eventsWithMissingVehiclesByDay.set(day, (eventsWithMissingVehiclesByDay.get(day) ?? 0) + 1);
      }
    }
    const warningsByDay = new Map<string, string>();
    for (const [day, count] of eventsWithMissingVehiclesByDay.entries()) {
      warningsByDay.set(day, count === 1 ? "1 Termin ohne Fahrzeug(e)" : `${count} Termine ohne Fahrzeug(e)`);
    }
    return { warningsByDay, warningsByEventId };
  }, [relevantDrivingLessons]);
  return result;
}

function getMissingVehicleWarnings(drivingLesson: Defined<DrivingLesson, "drivingLicenseClass">): string[] {
  const { resources, drivingLessonType, drivingLicenseClass, studentUsesOwnMotorcycle } = drivingLesson;
  let { vehicleId, vehicle2Id } = drivingLesson;
  if (resources && !vehicleId && !vehicle2Id) {
    vehicleId = resources[0];
    vehicle2Id = resources[1];
  }
  const vehicleIsMissing = !vehicleId;
  const vehicle2IsMissing = !vehicle2Id;
  if (!vehicleIsMissing && !vehicle2IsMissing) {
    // Fast return ...
    return [];
  }
  if (drivingLessonType === "simulator") {
    return vehicleIsMissing ? ["Noch kein Simulator ausgewählt"] : [];
  }
  const warnings: string[] = [];
  if (isForMotorcycle(drivingLicenseClass)) {
    if (vehicleIsMissing && !studentUsesOwnMotorcycle) {
      warnings.push("Noch kein Motorrad ausgewählt");
    }
    if (vehicle2IsMissing) {
      warnings.push("Noch kein Begleitfahrzeug ausgewählt");
    }
    return warnings;
  }
  if (isForCar(drivingLicenseClass)) {
    if (vehicleIsMissing) {
      warnings.push("Noch kein PKW ausgewählt");
    }
  } else if (isForTruck(drivingLicenseClass)) {
    if (vehicleIsMissing) {
      warnings.push("Noch kein LKW ausgewählt");
    }
  } else if (isForBus(drivingLicenseClass)) {
    if (vehicleIsMissing) {
      warnings.push("Noch kein Bus ausgewählt");
    }
  } else if (isForTractor(drivingLicenseClass)) {
    if (vehicleIsMissing) {
      warnings.push("Noch kein Traktor ausgewählt");
    }
  } else {
    throw new Error(`Unexpected drivingLicenseClass: ${drivingLicenseClass}`);
  }
  if (isWithTrailer(drivingLicenseClass) && vehicle2IsMissing) {
    warnings.push("Noch kein Anhänger ausgewählt");
  }
  return warnings;
}
